import { useState } from 'react'
import { useNavigate, useOutletContext } from 'react-router-dom'
import { useDropdownOptions } from '../../utils/useDropdownOptions'
import Step1_ClientProfile from './Step1_ClientProfile'
import Step2_FunctionScope from './Step2_FunctionScope'
import Step3_Generate from './Step3_Generate'
import type { ClientProfile, ClientBranding, AppVariant } from '@shared/types'
import { deriveBrandingVariants } from '../../utils/branding'

export interface OutsideInProps {
  /** Pins the analysis to a fixed set of functions and drops the Function Scope step. */
  lockedFunctionScope?: string[]
  /** Marks requests as coming from a variant app; gates all variant-only behaviour server-side. */
  variant?: AppVariant
  /** Renders the client platform dropdown in Step 1. */
  showPlatformSelector?: boolean
}

export default function OutsideIn({ lockedFunctionScope, showPlatformSelector, variant }: OutsideInProps = {}) {
  const STEPS = lockedFunctionScope
    ? ['Client Profile', 'Generate']
    : ['Client Profile', 'Function Scope', 'Generate']
  const generateStep = lockedFunctionScope ? 1 : 2

  const [step, setStep] = useState(0)
  const [profile, setProfile] = useState<Partial<ClientProfile>>({
    companyName: '',
    functionScope: lockedFunctionScope ?? [],
    variant,
    ambitionLevel: 3,
  })
  const [branding, setBranding] = useState<Partial<ClientBranding>>({
    primaryColor: '#A100FF',
    isWip: true,
    versionLabel: 'V1',
  })
  const [mmsId, setMmsId] = useState('')
  const [purposeOption, setPurposeOption] = useState('')
  const { industries, purposeOptions } = useDropdownOptions()
  const purpose = purposeOptions.find((o) => o.value === purposeOption)?.label ?? ''
  const navigate = useNavigate()
  const { refreshProjects } = useOutletContext<{ refreshProjects: () => Promise<void> }>()

  const updateBranding = (color: string) => {
    const variants = deriveBrandingVariants(color)
    setBranding((prev) => ({
      ...prev,
      primaryColor: color,
      primaryColorDark: variants.primaryColorDark,
      primaryColorLight: variants.primaryColorLight,
      isWip: true,
    }))
  }

  const updateAccent = (color: string | undefined) => {
    setBranding((prev) => ({ ...prev, accentColor: color }))
  }

  const updateLogo = (logoDataUrl: string | undefined) => {
    setBranding((prev) => ({ ...prev, logoDataUrl }))
  }

  const updateVersion = (versionLabel: string) => {
    setBranding((prev) => ({ ...prev, versionLabel }))
  }

  return (
    <div className="flex flex-col min-h-full p-8 animate-fade-in">
      {/* Progress steps */}
      <div className="flex items-center gap-0 mb-10 max-w-lg">
        {STEPS.map((label, i) => (
          <div key={i} className="flex items-center flex-1">
            <div className="flex items-center gap-2">
              <div
                className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                  i < step
                    ? 'bg-purple-600 text-white'
                    : i === step
                    ? 'bg-purple-600 text-white ring-4 ring-purple-100'
                    : 'bg-gray-200 text-gray-400'
                }`}
              >
                {i < step ? '✓' : i + 1}
              </div>
              <span
                className={`text-sm font-medium ${
                  i <= step ? 'text-gray-900' : 'text-gray-400'
                }`}
              >
                {label}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div
                className={`flex-1 h-0.5 mx-3 transition-all ${
                  i < step ? 'bg-purple-500' : 'bg-gray-200'
                }`}
              />
            )}
          </div>
        ))}
      </div>

      {/* Step content */}
      <div className="flex-1">
        {step === 0 && (
          <Step1_ClientProfile
            profile={profile}
            branding={branding}
            mmsId={mmsId}
            purposeOption={purposeOption}
            industries={industries}
            purposeOptions={purposeOptions}
            onProfileChange={setProfile}
            onMmsIdChange={setMmsId}
            onPurposeOptionChange={setPurposeOption}
            onColorChange={updateBranding}
            onAccentChange={updateAccent}
            onLogoChange={updateLogo}
            onVersionChange={updateVersion}
            showPlatformSelector={showPlatformSelector}
            onBack={() => navigate('/new')}
            onNext={() => setStep(1)}
          />
        )}
        {!lockedFunctionScope && step === 1 && (
          <Step2_FunctionScope
            profile={profile}
            onProfileChange={setProfile}
            onBack={() => setStep(0)}
            onNext={() => setStep(2)}
          />
        )}
        {step === generateStep && profile.companyName && profile.functionScope && profile.ambitionLevel !== undefined && (
          <Step3_Generate
            profile={profile as (Partial<ClientProfile> & Pick<ClientProfile, 'companyName' | 'functionScope' | 'ambitionLevel'>)}
            branding={{ ...(branding as ClientBranding), clientName: profile.companyName }}
            mmsId={mmsId}
            purpose={purpose}
            onBack={() => setStep(generateStep - 1)}
            onDone={async (projectId) => { await refreshProjects().catch(() => {}); navigate(`/dashboard/${projectId}`) }}
          />
        )}
      </div>
    </div>
  )
}
